<script setup>
import HrIcon from '../primitives/HrIcon.vue';
import HrItem from '../primitives/HrItem.vue';
import HrItemLabel from '../primitives/HrItemLabel.vue';
import HrItemSection from '../primitives/HrItemSection.vue';

/**
 * Single row for a menu — label, optional caption, leading icon, and trailing shortcut text.
 *
 * **Order is part of the API.** A menu is read top to bottom under a pointer that is already
 * moving, so the two ends carry meaning the middle does not:
 *
 * - **The primary action leads.** Whatever the surface already does on its own — the row click on
 *   a records row, the default of a split button — is the menu's first entry. The menu states the
 *   primary action rather than avoiding repeating it; a reader who opens the menu to find the
 *   obvious thing missing concludes it cannot be done here.
 * - **Destructive goes last, behind a divider.** Last is the position a mis-tap is least likely to
 *   reach on the way to something else, and the only one that stays last as entries are added. A
 *   `destructive` item with entries under it is a bug, not a layout choice.
 *
 * Between those two, group by what changes and separate the groups with `HrDivider`.
 *
 * **Picking a row closes the menu it is in**, which is what a menu is for: the reader came to
 * choose one thing, and a menu left open while they act behind it reads as a control that did not
 * register. Quasar's `v-close-popup` is what does it, and it goes on the row at the call site:
 *
 * ```
 * <hr-menu-item v-close-popup :label="..." @click="..." />
 * ```
 *
 * HrActionMenu adds it for you. A hand-composed menu has to, and the exception that leaves it off
 * is a menu whose rows are a multi-select rather than a choice. It cannot live inside this
 * component: the directive binds to whichever element the row happens to render, and the row's
 * root is Quasar's to choose.
 *
 * @status recommended
 * @group actions
 * @useWhen A single actionable entry inside an HrMenu or HrAdaptiveMenu
 * @avoidWhen List content outside a menu — use HrListItem
 * @related HrActionMenu
 * @related HrAdaptiveMenu
 * @a11y Renders with role="menuitem". Keyboard activation (Enter / Space) is handled by the underlying HrItem.
 * `active` also sets aria-current, so the selected entry is exposed as state rather than only tinted.
 */
defineOptions({ name: 'HrMenuItem' });

const emit = defineEmits({
  /** Fired when the item is activated. */
  click: null,
});

defineProps({
  /**
   * Item label text, clamped to one line with an ellipsis. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Copy
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Material icon name for the leading section.
   * Use the #leading slot for custom leading content.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * A second line under the label, explaining what choosing this item means.
   *
   * Use it where the consequence is not obvious from the name, so the explanation reaches the person
   * choosing at the moment it is relevant rather than sitting under the control as helper text
   * everyone reads and nobody needs.
   *
   * Named and rendered exactly as `HrListItem`'s caption, and as the caption on a list-select option.
   * Two-line rows sit closer to M3's list item spec than its menu spec, so reusing that role keeps
   * this token-true rather than minting a type or color role for menus alone.
   */
  caption: {
    type: String,
    default: null,
  },
  /**
   * Trailing text shown on the right side (e.g. keyboard shortcut label).
   * Use the #trailing slot for trailing icons or controls.
   */
  trailingText: {
    type: String,
    default: null,
  },
  /**
   * Marks the item as the currently selected one, e.g. the saved view a table is showing.
   * Defaults to null so a router-linked item keeps Quasar's own active detection.
   */
  active: {
    type: Boolean,
    default: null,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Applies destructive/danger styling. Such an item belongs last in its menu, behind a divider:
   * see the ordering rule above.
   */
  destructive: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <hr-item
    v-ripple
    clickable
    class="hr-menu-item"
    role="menuitem"
    :active="active"
    :aria-current="active === true ? 'true' : undefined"
    :disable="disable"
    :destructive="destructive"
    @click="emit('click', $event)"
  >
    <hr-item-section
      v-if="$slots.leading || icon"
      avatar
    >
      <!-- @slot Leading section — defaults to the `icon` prop rendered as a Material icon. -->
      <slot name="leading">
        <hr-icon :name="icon" />
      </slot>
    </hr-item-section>

    <hr-item-section>
      <!-- @slot Item label / content. Overrides the `label` prop when provided. -->
      <slot>
        <!--
          One line, cut with an ellipsis. The label's section is sized by the room left over rather
          than by its text, and without the clamp the text simply draws past it — a name with no
          space to break at runs under the trailing control instead of stopping before it.
        -->
        <hr-item-label
          v-if="label !== null"
          :lines="1"
        >
          {{ label }}
        </hr-item-label>
        <hr-item-label
          v-if="caption !== null"
          caption
          :lines="2"
        >
          {{ caption }}
        </hr-item-label>
      </slot>
    </hr-item-section>

    <hr-item-section
      v-if="$slots.trailing || trailingText !== null"
      side
    >
      <!-- @slot Trailing section — defaults to `trailingText` rendered as a caption. -->
      <slot name="trailing">
        <hr-item-label caption>
          {{ trailingText }}
        </hr-item-label>
      </slot>
    </hr-item-section>

    <!--
      Quasar anchors a popup to its parent, so one placed in another slot opens from that section
      rather than from the row.
    -->
    <!-- @slot Overlay anchored to the whole row — a submenu, tooltip or popup proxy. -->
    <slot name="popup" />
  </hr-item>
</template>

<style lang="scss">

/*
 * M3 menu items use label-large (14sp/500w) typography, but keep the full
 * 48dp item height. Quasar's dense prop achieves the font change but also
 * collapses the height — so we apply label-large directly instead.
 *
 * Matched on the root class rather than scoped: Vue rewrites `[data-theme] :deep(x)` in a scoped
 * block to `[data-theme][data-v-id] x`, which wants the theme attribute and the scope id on one
 * element. data-theme sits on <html> and the scope id on the item, so the scoped form matched
 * nothing and menu labels fell through to the body size.
 */
[data-theme] .hr-menu-item .hr-item-label:not(.q-item__label--caption) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

/*
 * Quasar reserves a 56dp column for a leading icon, which is a list-item measure — room for an
 * avatar. A menu icon is 24dp, so the column leaves the label stranded a long way from the thing
 * that introduces it, and the shorter the label the worse it reads.
 *
 * Not gated on the theme, unlike the typography above: the gap between an icon and the label it
 * introduces is the same question whichever palette is painting them, and gating it left the
 * legacy theme with the stranded label this exists to fix.
 *
 * Unscoped and matched on the root class because the scope id is on the item while the rule has to
 * reach a section Quasar renders inside it.
 */
.hr-menu-item .q-item__section--avatar {
  min-width: auto;
  padding-inline-end: var(--app-comp-menu-item-leading-gap);
}

</style>
